import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from '../i18n';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ScrollArea } from '../components/ui/scroll-area';
import { Switch } from '../components/ui/switch';
import { 
  Plus, Search, Users, Shield, User, Mail, Edit, Trash2, 
  Key, CheckCircle, XCircle
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const UsersPage = () => {
  const { user } = useAuth();
  const t = useTranslation(user?.language || 'ja');
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isPasswordOpen, setIsPasswordOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  
  const [form, setForm] = useState({
    email: '',
    password: '',
    name: '',
    role: 'user',
    language: 'ja',
  });
  
  const [newPassword, setNewPassword] = useState('');

  useEffect(() => {
    fetchUsers();
  }, [search]);

  const fetchUsers = async () => {
    try {
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      
      const { data } = await axios.get(`${API_URL}/api/users?${params}`, {
        withCredentials: true,
      });
      setUsers(data.users);
    } catch (e) {
      console.error('Failed to fetch users:', e);
      if (e.response?.status === 403) {
        toast.error(user?.language === 'ja' ? '管理者権限が必要です' : 'Admin access required');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    if (!form.email || !form.password || !form.name) {
      toast.error(user?.language === 'ja' ? '必須項目を入力してください' : 'Please fill required fields');
      return;
    }
    
    try {
      await axios.post(`${API_URL}/api/users`, form, {
        withCredentials: true,
      });
      toast.success(t.common.success);
      setIsCreateOpen(false);
      setForm({ email: '', password: '', name: '', role: 'user', language: 'ja' });
      fetchUsers();
    } catch (e) {
      toast.error(e.response?.data?.detail || t.common.error);
    }
  };

  const handleUpdate = async () => {
    if (!selectedUser) return;
    
    try {
      const updateData = {
        name: form.name,
        email: form.email,
        role: form.role,
        language: form.language,
      };
      
      await axios.put(`${API_URL}/api/users/${selectedUser.id}`, updateData, {
        withCredentials: true,
      });
      toast.success(t.common.success);
      setIsEditOpen(false);
      fetchUsers();
    } catch (e) {
      toast.error(e.response?.data?.detail || t.common.error);
    }
  };

  const handleDelete = async (userId) => {
    if (!window.confirm(t.common.confirm)) return;
    
    try {
      await axios.delete(`${API_URL}/api/users/${userId}`, {
        withCredentials: true,
      });
      toast.success(t.common.success);
      fetchUsers();
    } catch (e) {
      toast.error(e.response?.data?.detail || t.common.error);
    }
  };

  const handleResetPassword = async () => {
    if (!selectedUser || !newPassword || newPassword.length < 6) {
      toast.error(user?.language === 'ja' ? 'パスワードは6文字以上必要です' : 'Password must be at least 6 characters');
      return;
    }
    
    try {
      await axios.put(`${API_URL}/api/users/${selectedUser.id}/password?new_password=${encodeURIComponent(newPassword)}`, {}, {
        withCredentials: true,
      });
      toast.success(t.common.success);
      setIsPasswordOpen(false);
      setNewPassword('');
    } catch (e) {
      toast.error(e.response?.data?.detail || t.common.error);
    }
  };

  const handleToggleActive = async (userId, currentStatus) => {
    try {
      await axios.put(`${API_URL}/api/users/${userId}`, { is_active: !currentStatus }, {
        withCredentials: true,
      });
      fetchUsers();
    } catch (e) {
      toast.error(e.response?.data?.detail || t.common.error);
    }
  };

  const openEditDialog = (userItem) => {
    setSelectedUser(userItem);
    setForm({
      email: userItem.email || '',
      password: '',
      name: userItem.name || '',
      role: userItem.role || 'user',
      language: userItem.language || 'ja',
    });
    setIsEditOpen(true);
  };

  const openPasswordDialog = (userItem) => {
    setSelectedUser(userItem);
    setNewPassword('');
    setIsPasswordOpen(true);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return new Intl.DateTimeFormat(user?.language === 'en' ? 'en-US' : 'ja-JP', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    }).format(date);
  };

  const getRoleBadge = (role) => {
    if (role === 'admin') {
      return <Badge className="bg-primary/20 text-primary border-primary/30"><Shield className="w-3 h-3 mr-1" />{user?.language === 'ja' ? '管理者' : 'Admin'}</Badge>;
    }
    return <Badge variant="outline"><User className="w-3 h-3 mr-1" />{user?.language === 'ja' ? '一般' : 'User'}</Badge>;
  };

  // Check if current user is admin
  if (user?.role !== 'admin') {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <Card className="card-cyber p-8 text-center">
          <Shield className="w-16 h-16 mx-auto mb-4 text-destructive opacity-50" />
          <p className="text-lg font-medium">{user?.language === 'ja' ? '管理者権限が必要です' : 'Admin Access Required'}</p>
          <p className="text-muted-foreground mt-2">{user?.language === 'ja' ? 'このページにアクセスするには管理者権限が必要です' : 'You need admin privileges to access this page'}</p>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6" data-testid="users-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-glow">{user?.language === 'ja' ? 'ユーザー管理' : 'User Management'}</h1>
          <p className="text-sm text-muted-foreground mt-1">
            {user?.language === 'ja' ? 'ユーザーの作成・編集・削除' : 'Create, edit, and delete users'}
          </p>
        </div>
        
        <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
          <DialogTrigger asChild>
            <Button className="btn-primary w-full sm:w-auto" data-testid="add-user-btn">
              <Plus className="w-4 h-4 mr-2" />
              {user?.language === 'ja' ? 'ユーザー追加' : 'Add User'}
            </Button>
          </DialogTrigger>
          <DialogContent className="card-cyber border-glow max-w-[95vw] sm:max-w-lg">
            <DialogHeader>
              <DialogTitle>{user?.language === 'ja' ? '新規ユーザー' : 'New User'}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>{user?.language === 'ja' ? '名前' : 'Name'} *</Label>
                <Input
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="input-cyber"
                  data-testid="user-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label>{user?.language === 'ja' ? 'メール' : 'Email'} *</Label>
                <Input
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="input-cyber"
                  data-testid="user-email-input"
                />
              </div>
              <div className="space-y-2">
                <Label>{user?.language === 'ja' ? 'パスワード' : 'Password'} *</Label>
                <Input
                  type="password"
                  value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                  className="input-cyber"
                  data-testid="user-password-input"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>{user?.language === 'ja' ? '権限' : 'Role'}</Label>
                  <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                    <SelectTrigger className="input-cyber" data-testid="user-role-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="user">{user?.language === 'ja' ? '一般' : 'User'}</SelectItem>
                      <SelectItem value="admin">{user?.language === 'ja' ? '管理者' : 'Admin'}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>{user?.language === 'ja' ? '言語' : 'Language'}</Label>
                  <Select value={form.language} onValueChange={(v) => setForm({ ...form, language: v })}>
                    <SelectTrigger className="input-cyber">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="ja">日本語</SelectItem>
                      <SelectItem value="en">English</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <Button onClick={handleCreate} className="w-full btn-primary" data-testid="user-submit-btn">
                {t.common.save}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Search */}
      <div className="relative w-full sm:w-64">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={t.search.placeholder}
          className="pl-9 input-cyber"
          data-testid="user-search-input"
        />
      </div>

      {/* Users List */}
      <Card className="card-cyber">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center gap-2">
            <Users className="w-5 h-5 text-primary" />
            {user?.language === 'ja' ? 'ユーザー一覧' : 'User List'}
            <Badge variant="outline" className="ml-2 font-mono">{users.length}</Badge>
          </CardTitle>
        </CardHeader>
        
        <CardContent className="p-0">
          {loading ? (
            <div className="p-8 text-center text-muted-foreground">{t.common.loading}</div>
          ) : users.length === 0 ? (
            <div className="p-12 text-center">
              <Users className="w-16 h-16 mx-auto mb-4 text-muted-foreground opacity-30" />
              <p className="text-muted-foreground">{user?.language === 'ja' ? 'ユーザーがいません' : 'No users found'}</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="table-cyber">
                <thead>
                  <tr>
                    <th>{user?.language === 'ja' ? 'ユーザー' : 'User'}</th>
                    <th>{user?.language === 'ja' ? '権限' : 'Role'}</th>
                    <th>{user?.language === 'ja' ? 'ステータス' : 'Status'}</th>
                    <th>{user?.language === 'ja' ? '作成日' : 'Created'}</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((userItem) => (
                    <tr key={userItem.id} data-testid={`user-row-${userItem.id}`}>
                      <td>
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center">
                            <span className="text-sm font-medium text-primary">
                              {userItem.name?.charAt(0).toUpperCase()}
                            </span>
                          </div>
                          <div>
                            <p className="font-medium">{userItem.name}</p>
                            <p className="text-xs text-muted-foreground flex items-center gap-1">
                              <Mail className="w-3 h-3" />
                              {userItem.email}
                            </p>
                          </div>
                        </div>
                      </td>
                      <td>{getRoleBadge(userItem.role)}</td>
                      <td>
                        <div className="flex items-center gap-2">
                          {userItem.is_active !== false ? (
                            <Badge className="bg-green-500/20 text-green-400 border-green-500/30">
                              <CheckCircle className="w-3 h-3 mr-1" />
                              {user?.language === 'ja' ? '有効' : 'Active'}
                            </Badge>
                          ) : (
                            <Badge className="bg-destructive/20 text-destructive border-destructive/30">
                              <XCircle className="w-3 h-3 mr-1" />
                              {user?.language === 'ja' ? '無効' : 'Inactive'}
                            </Badge>
                          )}
                        </div>
                      </td>
                      <td className="font-mono text-sm">{formatDate(userItem.created_at)}</td>
                      <td>
                        <div className="flex items-center gap-1">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => openEditDialog(userItem)}
                            data-testid={`edit-user-${userItem.id}`}
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => openPasswordDialog(userItem)}
                            data-testid={`password-user-${userItem.id}`}
                          >
                            <Key className="w-4 h-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="text-destructive hover:text-destructive"
                            onClick={() => handleDelete(userItem.id)}
                            disabled={userItem.id === user?.id}
                            data-testid={`delete-user-${userItem.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Edit Dialog */}
      <Dialog open={isEditOpen} onOpenChange={setIsEditOpen}>
        <DialogContent className="card-cyber border-glow">
          <DialogHeader>
            <DialogTitle>{user?.language === 'ja' ? 'ユーザー編集' : 'Edit User'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>{user?.language === 'ja' ? '名前' : 'Name'}</Label>
              <Input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="input-cyber"
              />
            </div>
            <div className="space-y-2">
              <Label>{user?.language === 'ja' ? 'メール' : 'Email'}</Label>
              <Input
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="input-cyber"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{user?.language === 'ja' ? '権限' : 'Role'}</Label>
                <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                  <SelectTrigger className="input-cyber">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="user">{user?.language === 'ja' ? '一般' : 'User'}</SelectItem>
                    <SelectItem value="admin">{user?.language === 'ja' ? '管理者' : 'Admin'}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>{user?.language === 'ja' ? '言語' : 'Language'}</Label>
                <Select value={form.language} onValueChange={(v) => setForm({ ...form, language: v })}>
                  <SelectTrigger className="input-cyber">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ja">日本語</SelectItem>
                    <SelectItem value="en">English</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <Button onClick={handleUpdate} className="w-full btn-primary">
              {t.common.save}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Password Reset Dialog */}
      <Dialog open={isPasswordOpen} onOpenChange={setIsPasswordOpen}>
        <DialogContent className="card-cyber border-glow">
          <DialogHeader>
            <DialogTitle>{user?.language === 'ja' ? 'パスワードリセット' : 'Reset Password'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              {selectedUser?.name} ({selectedUser?.email})
            </p>
            <div className="space-y-2">
              <Label>{user?.language === 'ja' ? '新しいパスワード' : 'New Password'}</Label>
              <Input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className="input-cyber"
                placeholder={user?.language === 'ja' ? '6文字以上' : 'At least 6 characters'}
                data-testid="new-password-input"
              />
            </div>
            <Button onClick={handleResetPassword} className="w-full btn-primary" data-testid="reset-password-btn">
              {user?.language === 'ja' ? 'パスワードを更新' : 'Update Password'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default UsersPage;
